import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface ChatMessage {
  id: string;
  user_email: string;
  message: string;
  sender_type: 'user' | 'bot';
  request_id?: string;
  chat_session_id: string;
  created_at: string;
}

export const useChat = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(() => crypto.randomUUID());
  const { toast } = useToast();

  // User E-Mail aus Auth holen
  const [userEmail, setUserEmail] = useState<string>('');
  
  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.email) {
        setUserEmail(user.email);
      }
    };
    getUser();
  }, []);

  // Nachrichten der aktuellen Session laden
  const loadMessages = useCallback(async () => {
    if (!userEmail) return;
    
    const { data, error } = await supabase
      .from('chat_messages')
      .select('*')
      .eq('user_email', userEmail)
      .eq('chat_session_id', currentSessionId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error loading messages:', error);
      return;
    }

    setMessages((data || []) as ChatMessage[]);
  }, [userEmail, currentSessionId]);

  // Realtime Subscription für neue Nachrichten
  useEffect(() => {
    if (!userEmail) return;

    const channel = supabase
      .channel(`chat-updates-${currentSessionId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'chat_messages',
        filter: `user_email=eq.${userEmail}`
      }, (payload) => {
        const newMessage = payload.new as ChatMessage;
        
        // Nur Nachrichten der aktuellen Session hinzufügen
        if (newMessage.chat_session_id === currentSessionId) {
          setMessages(prev => {
            // Prüfe ob Nachricht bereits existiert (verhindert Duplikate)
            const exists = prev.some(msg => msg.id === newMessage.id);
            if (exists) {
              return prev;
            }
            
            // Nur Bot-Nachrichten über Realtime hinzufügen (User-Nachrichten werden lokal hinzugefügt)
            if (newMessage.sender_type === 'bot') {
              setIsLoading(false); // Stop loading when bot responds
              return [...prev, newMessage];
            }
            
            return prev;
          });
        }
      })
      .subscribe();

    return () => {
      try {
        supabase.removeChannel(channel);
      } catch (e) {
        console.debug('Channel cleanup skipped:', e);
      }
    };
  }, [userEmail, currentSessionId]);

  // Nachrichten beim Session-Wechsel laden
  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  // Nachricht senden
  const sendMessage = useCallback(async (message: string) => {
    if (!userEmail || !message.trim()) return;

    setIsLoading(true);
    const requestId = crypto.randomUUID();

    try {
      // User-Nachricht sofort in UI anzeigen
      const userMessage: ChatMessage = {
        id: crypto.randomUUID(),
        user_email: userEmail,
        message: message.trim(),
        sender_type: 'user',
        request_id: requestId,
        chat_session_id: currentSessionId,
        created_at: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, userMessage]);

      // An Edge Function senden
      const { error } = await supabase.functions.invoke('send-chat-to-make', {
        body: {
          user_email: userEmail,
          message: message.trim(),
          request_id: requestId,
          chat_session_id: currentSessionId
        }
      });

      if (error) {
        throw error;
      }

    } catch (error) {
      console.error('Error sending message:', error);
      setIsLoading(false); // Only stop loading on error
      toast({
        title: "Fehler",
        description: "Nachricht konnte nicht gesendet werden.",
        variant: "destructive",
      });
    }
  }, [userEmail, currentSessionId, toast]);

  // Chat restarten (neue Session)
  const clearChat = useCallback(() => {
    setCurrentSessionId(crypto.randomUUID());
    setMessages([]);
    toast({
      title: "Chat wurde geleert",
      description: "Ein neues Gespräch wurde gestartet.",
    });
  }, [toast]);

  return {
    messages,
    isOpen,
    isLoading,
    setIsOpen,
    sendMessage,
    clearChat
  };
};